import { Injectable } from '@angular/core';
import { Router, NavigationExtras } from '@angular/router';
import { AnimationController, NavController } from '@ionic/angular/standalone';

/**
 * Service de navigation avec animations directionnelles
 * Gère les transitions gauche/droite selon l'ordre du menu bottom
 */
@Injectable({
  providedIn: 'root',
})
export class NavigationService {
  // Ordre des routes dans le menu bottom (de gauche à droite)
  // Note: quand non connecté, l'ordre est: home, magasins, login, more
  // Note: quand connecté, l'ordre est: home, products, magasins, cart, profile, more
  private routeOrder = ['/home', '/products', '/magasins', '/login', '/cart', '/profile', '/more'];

  constructor(
    private router: Router,
    private navCtrl: NavController,
    private animationCtrl: AnimationController
  ) {}

  /**
   * Navigue vers une route avec l'animation appropriée
   * @param targetRoute - Route de destination
   * @param currentRoute - Route actuelle (optionnel, auto-détecté si non fourni)
   */
  navigateWithAnimation(targetRoute: string, currentRoute?: string) {
    const current = currentRoute || this.router.url;
    const direction = this.getDirection(current, targetRoute);

    // Utiliser le NavController avec la bonne méthode selon la direction
    if (direction === 'forward') {
      this.navCtrl.navigateForward(targetRoute);
    } else {
      this.navCtrl.navigateBack(targetRoute);
    }
  }

  /**
   * Détermine la direction de l'animation selon les positions des routes
   * @param fromRoute - Route de départ
   * @param toRoute - Route d'arrivée
   * @returns 'forward' pour gauche->droite, 'back' pour droite->gauche
   */
  private getDirection(
    fromRoute: string,
    toRoute: string
  ): 'forward' | 'back' {
    const fromIndex = this.getRouteIndex(fromRoute);
    const toIndex = this.getRouteIndex(toRoute);

    // Si on va vers la droite (index augmente) -> forward
    // Si on va vers la gauche (index diminue) -> back
    return toIndex > fromIndex ? 'forward' : 'back';
  }

  /**
   * Récupère l'index d'une route dans le menu
   * @param route - Chemin de la route
   * @returns Index de la route (ou -1 si non trouvée)
   */
  private getRouteIndex(route: string): number {
    // Nettoyer la route (enlever les query params et fragments)
    const cleanRoute = route.split('?')[0].split('#')[0];

    // Chercher la route exacte
    let index = this.routeOrder.findIndex((r) => r === cleanRoute);

    // Si pas trouvée exactement, chercher si la route commence par un des chemins
    if (index === -1) {
      index = this.routeOrder.findIndex((r) =>
        cleanRoute.startsWith(r + '/')
      );
    }

    // Si toujours pas trouvée, considérer comme étant à la fin
    return index !== -1 ? index : this.routeOrder.length;
  }

  /**
   * Navigue simplement sans animation spéciale
   * @param route - Route de destination
   */
  navigate(route: string) {
    this.router.navigate([route]);
  }
}
